import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import {validateEmail} from '../../services/validation';
import { getBBDDLexon, getContactsLexon } from '../../services/contacts';
import IconRemove from '../../assets/icons/close-round.svg?react';
import {
  filterContactsBySearch,
  calculateSearchRelevance,
  calculateStringRelevance,
  removeDuplicateContacts
} from '../../utils/contacts-utils';

// Module-level cache shared across all HeaderAddress instances (To, Cc, Bcc)
let _sharedLexonContactsCache = null;
let _sharedLexonContactsPromise = null;
let _sharedLexonContactsCacheKey = null; // To invalidate cache if user changes

export class HeaderAddress extends Component {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
    this.containerRef = React.createRef();
    this.handleOnSuggestionChange = this.onSuggestionChange.bind(this);
    this.handleOnSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.handleOnSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.handleOnSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.handleOnHeaderKeyDown = this.onHeaderKeyDown.bind(this);
    this.handleOnHeaderBlur = this.onHeaderBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleAddressRemove = this.handleAddressRemove.bind(this);
    this.handleOnHeaderPaste = this.onHeaderPaste.bind(this);
    this.validateEmailString = this.validateEmailString.bind(this);
    this.state = {
      value: '',
      suggestions: [],
      nameEmailMap: {},
      lexonContactsReady: false
    };
    this._isMounted = false;
    this.contactSourceMap = new Map();
    this.callIndex = 0;
  }

  componentDidMount() {
    this._isMounted = true;
    // Preload Lexon contacts on mount
    this.loadLexonContactsOnce();
  }

  componentDidUpdate(prevProps, prevState) {
    // When Lexon contacts cache is loaded, refresh suggestions if there's a search value
    if (!prevState.lexonContactsReady && this.state.lexonContactsReady) {
      if (this.state.value.length > 0) {
        this.onSuggestionsFetchRequested({ value: this.state.value });
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.callIndex = -1;
    _sharedLexonContactsCache = null;
    _sharedLexonContactsPromise = null;
    _sharedLexonContactsCacheKey = null;
  }

  extractEmailFromSuggestion = (suggestion) => {
    const cleanSuggestion = suggestion.replace(/\s*LexOn\s*$/, '');
    // Try angle brackets first: "Name" <email>
    let match = cleanSuggestion.match(/(.*)<(.*)>/);
    if (match) {
      return match[2].trim();
    }
    // Try parentheses: Name (email)
    match = cleanSuggestion.match(/(.*)\(([^)]+@[^)]+)\)/);
    if (match) {
      return match[2].trim();
    }
    return cleanSuggestion.trim();
  };

  removeDuplicatesById = arrayOfObjects => {
    const uniqueIds = [];
    return arrayOfObjects.filter(item => {
      if (!uniqueIds.includes(item.id)) {
        uniqueIds.push(item.id);
        return true;
      }
      return false;
    });
  };

  // Load Lexon contacts from API once and cache them (shared across all instances)
  async loadLexonContactsOnce() {
    if (!this.props.currentUser || !this.props.currentUser.idClienteNavision || !this.props.currentUser.idClienteLef) {
      return;
    }

    const env = this.props.currentUser?.env || 'PRE';
    const cacheKey = `${this.props.currentUser.idClienteNavision}_${this.props.currentUser.idClienteLef}_${env}`;

    // If cache exists and is for the same user, use it
    if (_sharedLexonContactsCache !== null && _sharedLexonContactsCacheKey === cacheKey) {
      if (this._isMounted && !this.state.lexonContactsReady) {
        this.setState({ lexonContactsReady: true });
      }
      return;
    }

    // If already loading, wait for the existing promise
    if (_sharedLexonContactsPromise !== null && _sharedLexonContactsCacheKey === cacheKey) {
      try {
        await _sharedLexonContactsPromise;
        if (this._isMounted && !this.state.lexonContactsReady) {
          this.setState({ lexonContactsReady: true });
        }
      } catch (err) {
        console.log('Error waiting for Lexon contacts:', err);
      }
      return;
    }

    // Start loading - set cache key immediately to prevent race conditions
    _sharedLexonContactsCacheKey = cacheKey;
    
    const loadContacts = async () => {
      try {
        const lexDataBases = await getBBDDLexon(
          this.props.currentUser.idClienteNavision,
          this.props.currentUser.idClienteLef,
          env,
          this.props.lexonToken
        );

        if (lexDataBases && lexDataBases.data !== null) {
          const lexUserId = lexDataBases.data.idUser;
          const contactsLexon = [];

          const contactPromises = lexDataBases.data.companies.map(company =>
            getContactsLexon(lexUserId, company.idCompany, company.bbdd, env, this.props.lexonToken)
              .then(contacts => {
                if (contacts && contacts.data && contacts.data.data) {
                  contacts.data.data.forEach(contact => {
                    if (contact.email && contact.email !== null) {
                      contact.id = `${contact.id}_${contact.idType}_${contact.entityType}_${company.bbdd}`;
                      contactsLexon.push(contact);
                    }
                  });
                }
              })
              .catch(err => {
                console.log('Error fetching contacts for company:', company.idCompany, err);
                return [];
              })
          );

          await Promise.all(contactPromises);
          _sharedLexonContactsCache = contactsLexon;
        } else {
          _sharedLexonContactsCache = [];
        }
      } catch (err) {
        console.log('Error loading Lexon contacts:', err);
        _sharedLexonContactsCache = [];
      } finally {
        _sharedLexonContactsPromise = null;
      }
    };

    _sharedLexonContactsPromise = loadContacts();
    await _sharedLexonContactsPromise;
    
    if (this._isMounted && !this.state.lexonContactsReady) {
      this.setState({ lexonContactsReady: true });
    }
  }

  // Get filtered Lexon contacts from shared cache
  getLexonContacts(searchValue = '') {
    // If not cached yet, trigger load and return empty
    if (_sharedLexonContactsCache === null) {
      this.loadLexonContactsOnce();
      return [];
    }

    // Filter cached contacts locally
    const filteredContacts = filterContactsBySearch(_sharedLexonContactsCache, searchValue);
    
    const contactObjects = filteredContacts.map(contact => ({
      displayName: contact.name && contact.email ? 
        `"${contact.name}" <${contact.email}>` : 
        contact.email || contact.name,
      email: contact.email,
      name: contact.name,
      isLexon: true,
      originalContact: contact,
      searchRelevance: calculateSearchRelevance(contact, searchValue)
    }));

    return contactObjects;
  }

  prioritizeSuggestions = (propContacts, lexonContacts, searchValue) => {
    const propContactObjects = (propContacts || []).map(contact => ({
      displayName: contact,
      isLexon: false,
      searchRelevance: calculateStringRelevance(contact, searchValue)
    }));

    const allContacts = [...propContactObjects, ...(lexonContacts || [])];
    
    // Remove duplicates by email+name, prioritizing LexON contacts
    const uniqueContacts = removeDuplicateContacts(allContacts);
    
    const sortedContacts = uniqueContacts.sort((a, b) => {
      if (a.searchRelevance !== b.searchRelevance) {
        return b.searchRelevance - a.searchRelevance;
      }
      if (a.isLexon !== b.isLexon) {
        return a.isLexon ? -1 : 1;
      }
      return a.displayName.localeCompare(b.displayName);
    });

    this.contactSourceMap = new Map();
    sortedContacts.forEach(contact => {
      this.contactSourceMap.set(contact.displayName, contact.isLexon);
    });

    const suggestions = sortedContacts.map(contact => contact.displayName);

    return suggestions;
  };

  handleAddressRemove(id, address) {
    const { onAddressRemove } = this.props;
    const newNameEmailMap = { ...this.state.nameEmailMap };
    delete newNameEmailMap[address];

    this.setState({
      nameEmailMap: newNameEmailMap
    });
    onAddressRemove(id, address);
  }

  handleKeyDown(event) {
    const { addresses, id } = this.props;
    if (event.key === 'Backspace' && addresses.length > 0 && this.state.value === '') {
      // Remove the selected address
      const addressToRemove = addresses[addresses.length - 1];
      this.handleAddressRemove(id, addressToRemove);
    }
  }

  onHeaderPaste = async (event) => {
    const pastedText = (event.clipboardData || window.clipboardData).getData('text');
    if (pastedText) {
      if (pastedText.includes(';') || pastedText.includes(',')) {
        event.preventDefault();
        const emails = pastedText
          .split(/[;,]+/)
          .map(email => email.trim())
          .filter(email => email.length > 0);
        for (const email of emails) {
          if (this.validateEmailString(email)) {
            this.props.onAddressAdd(this.props.id, email);
            await new Promise(resolve => setTimeout(resolve, 10));
            if (this.state.suggestions.length === 0) {
              this.props.handleNewContacts?.(email);
            }}
          }
        this.setState({ value: '' });
      }
    }
  };

  validateEmailString = (emailString) => {
    try {
      const error = validateEmail(emailString);
      return !error;
    } catch (e) {
      console.error(e)
      return false;
    }
  };


  render() {
    const {
      id,
      label,
      addresses
    } = this.props;
    const { suggestions, value } = this.state;
    return (
      <div
        ref={this.containerRef}
        className="input-group"
        onClick={() => this.fieldClick()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => this.onDrop(e, id)}
        onKeyDown={this.handleKeyDown}
      >
        <div className={"input-group-prepend"}>
          <span className="input-group-text">{label}</span>
        </div>
        <div className="group-input-container">
          {addresses.map((address, index) => {
            const displayName = this.state.nameEmailMap[address] || address;
            return (
              <div
                key={index}
                className={`mdc-chip`}
                draggable={true}
                onDragStart={event =>
                  HeaderAddress.onAddressDragStart(event, id, address)
                }
              >
                <div className={"mdc-chip__text"}>{displayName}</div>
                <IconRemove 
                  className="lf-icon lf-icon-close-round" 
                  onClick={() => this.handleAddressRemove(id, address)} 
                />
              </div>
            );
          })}
          <Autosuggest
            suggestions={suggestions}
            ref={this.inputRef}
            inputProps={{
              id: id,
              type: 'text',
              value: value,
              onChange: this.handleOnSuggestionChange,
              onKeyDown: this.handleOnHeaderKeyDown,
              onBlur: this.handleOnHeaderBlur
            }}
            getSuggestionValue={HeaderAddress.getSuggestionValue}
            renderSuggestion={this.renderSuggestion}
            onSuggestionsFetchRequested={this.handleOnSuggestionsFetchRequested}
            onSuggestionsClearRequested={this.handleOnSuggestionsClearRequested}
            onSuggestionSelected={this.handleOnSuggestionSelected}
            shouldRenderSuggestions={(val) => val.length >= 3}
            theme={{
              container: `header-address-container`,
              suggestionsContainer: `header-address-suggestions-container`,
              suggestionsContainerOpen: `header-address-suggestions-containerOpen`,
              suggestionsList: `header-address-suggestions-list`,
              suggestion: "header-address-suggestion",
              suggestionHighlighted: "header-address-suggestion-highlighted",
              onBlur: this.handleOnHeaderBlur,
              onPaste: this.onHeaderPaste
            }}
          />
        </div>
        <style>{`
          .header-address-suggestions-container {
            position: absolute;
            z-index: 1000;
            background-color: white;
            margin-top: 30px;
            left: 60px;
            right: 0;
          }

          .header-address-container {
            flex: 1;
            height: 22px;
            display: flex;
          }

          .header-address-container > input {
            border: none;
            padding-left: 4px;
            border-radius: 4px;
          }

          .group-input-container {
            width: calc(100% - 60px);
            border-radius: 4px;
            border: 1px solid var(--grey-400);
            align-items: center;
            display: flex;
            flex-wrap: wrap;
            padding: 7px;
            row-gap: 6px;
          }


          .header-address-container > input:focus {
            outline: none;
          }

          .header-address-suggestions-list {
            text-align: left;
            padding: 0px;
            max-height: 300px;
            overflow-y: auto;
            box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1),
                        4px 0px 6px rgba(0, 0, 0, 0.1),
                        -4px 0px 6px rgba(0, 0, 0, 0.1);
            border-radius: 5px;
          }

          .header-address-suggestion {
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: 0 20px 0 10px;
            height: 40px;
            box-sizing: border-box;
          }

          .header-address-suggestion:hover {
            background-color: #F2F3F8;
          }

          .header-address-suggestion-highlighted {
            background-color: rgba(0, 0, 0, 0.1);
          }

          .lexon-badge {
            background-color: #E5E8F1;
            color: black;
            font-size: 12px;
            padding: 0 8px;
            border-radius: 4px;
            margin-left: 8px;
            font-weight: 400;
            flex-shrink: 0;
            font-family: Inter, Arial, sans-serif;
            height: 22px;
            display: inline-flex;
            align-items: center;
            box-sizing: border-box;
          }

          .suggestion-content {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .suggestion-text {
          }

          .mdc-chip {
            border: 1px solid #99A3C9;
            border-radius: 20px;
            color: rgba(0, 0, 0, 0.87);
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 16px;
            font-weight: 400;
            display: inline-flex;
            position: relative;
            align-items: center;
            box-sizing: border-box;
            outline: none;
            overflow: hidden;
            background-color: white;
            padding: 6px 5px;

            height: 22px;
            margin: 0 5px 0 0 !important;
          }

          .mdc-chip i {
            cursor: pointer;
          }

          .mdc-chip__text {
            white-space: nowrap;
            font-size: 12px;
            margin-right: 5px;
          }
        `}</style>
      </div>
    );
  }

  renderSuggestion = (suggestionValue) => {
    const isLexonContact = this.contactSourceMap && this.contactSourceMap.get(suggestionValue);

    return (
      <div className="suggestion-content">
        <span className="suggestion-text">{suggestionValue}</span>
        {isLexonContact && <span className="lexon-badge">LexON</span>}
      </div>
    );
  };

  fieldClick() {
  if (this.inputRef.current && this.inputRef.current.input) {
    this.inputRef.current.input.focus();
  }
}

static clearValidation(target) {
  target.setCustomValidity('');
}

onSuggestionChange(event, {newValue}) {
  this.setState({value: newValue});
}

async onSuggestionsFetchRequested({value}) {
  if (this.props.id !== 'lexon') {
    this.callIndex += 1;
    const currentCall = this.callIndex;
    await new Promise(resolve => setTimeout(resolve, 300));

    if (currentCall === this.callIndex && currentCall !== -1 && value !== '' && this._isMounted) {
      try {
        const [propContacts, lexonContacts] = await Promise.all([
          Promise.resolve(this.props.getAddresses(value)),
          this.getLexonContacts(value)
        ]);

        if (this._isMounted && currentCall === this.callIndex) {
          const prioritizedSuggestions = this.prioritizeSuggestions(
            propContacts,
            lexonContacts,
            value
          );

          this.setState({
            suggestions: prioritizedSuggestions
          });
        }
      } catch (error) {
        console.log('Contact fetch failed:', error);
        try {
          const propContacts = this.props.getAddresses(value);
          if (this._isMounted && currentCall === this.callIndex) {
            this.setState({ suggestions: propContacts || [] });
          }
        } catch (fallbackError) {
          console.log('Even getAddresses failed:', fallbackError);
          if (this._isMounted && currentCall === this.callIndex) {
            this.setState({ suggestions: [] });
          }
        }
      }


    }
  } else {
    this.setState({ suggestions: this.props.interveners });
  }
}

onSuggestionsClearRequested() {
  if (this._isMounted) {
    this.setState({suggestions: []});
    this.contactSourceMap.clear();
  }
}

onSuggestionSelected(event, {suggestionValue}) {
  if (this._isMounted) {
    this.setState({value: '', suggestions: []});
  }
  const cleanSuggestionValue = suggestionValue.replace(/\s*LexOn\s*$/, '');
  const match = cleanSuggestionValue.match(/(.*)<(.*)>/);
  if (match) {
    const name = match[1].replace(/"/g, '').trim();
    const email = match[2].trim();
    if (email) {
      const emailExists = this.props.addresses.includes(email);

      this.setState(prevState => ({
        nameEmailMap: {
          ...prevState.nameEmailMap,
          [email]: name.length > 2 ? name : prevState.nameEmailMap[email] || email
        }
      }));

      if (!emailExists) {
        this.props.onAddressAdd(this.props.id, email);
      }
      return;
    }
  }

  const extractedEmail = this.extractEmailFromSuggestion(suggestionValue);
  const emailExists = this.props.addresses.includes(extractedEmail);

  if (!emailExists) {
    this.props.onAddressAdd(this.props.id, extractedEmail || cleanSuggestionValue);
  }
  setTimeout(() => HeaderAddress.clearValidation(this.inputRef.current.input));
}

static getSuggestionValue(suggestion) {
  return suggestion.replace(/\s*LexOn\s*$/, '');
}

validateEmail(event) {
  const target = event.target;
  const error = validateEmail(target.value);
  if (error) {
    event.preventDefault();
    target.setCustomValidity(error);
    setTimeout(() => target.reportValidity());
    return false;


  }
  return true;
}

onHeaderKeyDown(event) {
  const target = event.target;
  HeaderAddress.clearValidation(target);
  if (event.key === 'Enter' || event.key === ';') {
    if (this.validateEmail(event)) {
      const id = target.id;
      const value = target.value.replace(/;/g, '');
      this.props.onAddressAdd(id, value);
      if (this._isMounted) {
        this.setState({value: ''});



      }
      target.focus();
      event.preventDefault();
    }
  }
}

onHeaderBlur(event) {
  setTimeout(() => {
    const target = event.target;
    if (target?.value?.length > 0) {
      if (this.validateEmail(event)) {
        this.props.onAddressAdd(target.id, target.value);
        if (this._isMounted) {
          this.setState({value: ''});
        }
      }
    }
  }, 150);
}

static onAddressDragStart(event, id, address) {
  event.stopPropagation();
  const payload = {id, address};
  event.dataTransfer.setData('application/json', JSON.stringify(payload));
}

onDrop(event, id) {
  event.preventDefault();
  const types = event.dataTransfer.types;
  if (types && Array.from(types).indexOf('application/json') >= 0) {
    try {
      const payload = JSON.parse(event.dataTransfer.getData('application/json'));
      if (id && id !== payload.id) {
        const fromId = payload.id;
        const address = payload.address;
        this.props.onAddressMove(fromId, id, address);



      }
    } catch (error) {
      console.log('Error parsing drag and drop data:', error);
    }
  }
}
}

HeaderAddress.propTypes = {
  t: PropTypes.func,
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  chipClassName: PropTypes.string,
  autoSuggestClassName: PropTypes.string,
  autoSuggestMenuClassName: PropTypes.string,
  addresses: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.string,
  getAddresses: PropTypes.func,
  onAddressAdd: PropTypes.func,
  onAddressRemove: PropTypes.func,
  onAddressMove: PropTypes.func,
  interveners: PropTypes.array,
  currentUser: PropTypes.shape({
    env: PropTypes.string,
    idClienteNavision: PropTypes.string,
    idClienteLef: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
};

HeaderAddress.defaultProps = {
  className: '',
  chipClassName: '',
  autoSuggestClassName: '',
  autoSuggestMenuClassName: '',
  addresses: [],
  label: '',
  onAddressAdd: () => {},
  onAddressRemove: () => {},
  onAddressMove: () => {},
  interveners: [],
  lexon: {},
  currentUser: {}
};

export default HeaderAddress;